import React, { PureComponent } from 'react';
import { connectDeep } from 'redux-utils';
import { back, redirect } from 'redux-first-router';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { isiOS, APP_RECOMMENDATIONS, CLOCK_RECOMMENDATIONS } from 'config';
import App from 'models/app';
import AppList, { AppList as AppListSimple } from 'components/AppList';
import ClockList, { ClockList as ClockListSimple } from 'components/ClockList';
import Layout from 'components/Layout';
import SearchRecommendations from 'components/SearchRecommendations';
import LoadingPage from 'components/LoadingPage';
import ErrorPage from 'components/ErrorPage';
import SearchIconAndroid from 'icons/search-android.svg';
import SearchIconiOS from 'icons/search-ios-placeholder.svg';
import XIcon from 'icons/x-android.svg';
import i18n from 'i18n';
import { fontLight } from 'themes';
import { searchAllApps } from 'actions/registry';
import { PageRoutes } from '../../constants';

const BaseNav = styled.div`
  width: 100vw;
  display: flex;
`;

const AndroidNav = styled(BaseNav)`
  box-shadow: 0px 1px 3px #d4d4d4;
`;

const SearchForm = styled.form`
  flex-grow: 1;
  margin-right: 6px;
`;

const BaseInput = styled.input`
  margin-left: 10px;
  font-size: 14px;
  border: none;

  &:focus {
    outline: 0;
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
`;

const SearchInputAndroid = styled(BaseInput)`
  background: url(${SearchIconAndroid}) no-repeat;
  background-position: 6px;
  text-indent: 40px;
  line-height: 3;
  width: 100%;
`;

const SearchInputiOS = styled(BaseInput)`
  background: url(${SearchIconiOS}) no-repeat;
  background-position: 6px;
  text-indent: 28px;
  line-height: 1.6;
  width: 94%;
  background-color: #f3f3f3;
  border-radius: 29px;
`;

const BaseCloseLink = styled.a`
  cursor: pointer;
  font-size: 18px;
  margin-right: 10px;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  && {
    font-family: ${fontLight};
  }
`;

const CloseLinkiOS = styled(BaseCloseLink)`
  margin-top: 1.5px;
`;

const CloseIcon = styled.img`
  vertical-align: middle;
  margin-left: 16px;
`;

const SearchNav = isiOS ? BaseNav : AndroidNav;

const SearchInput = isiOS ? SearchInputiOS : SearchInputAndroid;

const CloseLink = isiOS ? CloseLinkiOS : BaseCloseLink;

const APP_DIGEST_PROPS = {
  showInstalled: true
};

const mapStateToProps = (state) => {
  const {
    direction,
    searchInput: { text = '', userSetInput, type }
  } = state.gallery;

  const {
    search: {
      loading: searchLoading,
      [`${text}-${type}`]: { entries = [], hasNextPage } = {}
    },
    installed: {
      loading: installedLoading,
      error: installedError,
      entries: installedEntries = []
    }
  } = state.registry;

  const loading = installedEntries.length > 0 ? false : installedLoading;

  const error = installedError;

  return {
    loading: loading || (entries.length === 0 && searchLoading),
    error,
    text,
    apps: entries.map((appId) => App.selectFromState(appId, state)),
    hasNextPage,
    direction,
    userSetInput
  };
};

const mapDispatchToProps = (dispatch, { isApp, payload: { appType } }) => ({
  updateSearchText(text) {
    dispatch(
      redirect({
        type: PageRoutes.SEARCH,
        payload: { appType },
        query: { text }
      })
    );
  },
  fetchMore: ({ text, startIndex }) =>
    dispatch(searchAllApps(isApp ? 'APP' : 'CLOCK', text, startIndex))
});

class Search extends PureComponent {
  static propTypes = {
    text: PropTypes.string,
    apps: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    isApp: PropTypes.bool,
    userSetInput: PropTypes.bool
  };

  state = {
    text: this.props.text
  };

  componentWillReceiveProps({ text }) {
    const trimmedText = text.trim();
    if (trimmedText && this.props.text.trim() !== trimmedText) {
      this.setState({
        text: trimmedText
      });
    }
  }

  // TODO: replace internal state with updating the Redux text state
  handleChange = (event) => {
    const text = event.target.value;
    this.setState({ text });
  };

  handleSearch = (event) => {
    event.preventDefault();
    this.props.updateSearchText(this.state.text);
  };

  handleRecSearch = (recommendation) => {
    this.props.updateSearchText(recommendation);
  };

  handleCancel = () => back();

  renderSearchNavContent() {
    const { isApp } = this.props;

    return (
      <SearchNav>
        <SearchForm onSubmit={this.handleSearch} action=".">
          <SearchInput
            type="search"
            id="search-input"
            placeholder={
              isApp
                ? i18n('page.Search.placeholderApps')()
                : i18n('page.Search.placeholderClocks')()
            }
            value={this.state.text}
            onChange={this.handleChange}
          />
        </SearchForm>
        <CloseLink onClick={this.handleCancel}>
          {isiOS ? (
            i18n('page.Search.cancel')()
          ) : (
            <CloseIcon src={XIcon} alt="" id="search-close-icon" />
          )}
        </CloseLink>
      </SearchNav>
    );
  }

  render() {
    const {
      loading,
      error,
      apps,
      text,
      isApp,
      userSetInput,
      fetchMore
    } = this.props;

    const ListPage = this.getList();
    let body;

    if (!text || apps.length === 0) {
      const recommendations = isApp
        ? APP_RECOMMENDATIONS
        : CLOCK_RECOMMENDATIONS;
      body = (
        <SearchRecommendations
          noResults={apps.length === 0 && text.length > 0}
          recommendations={recommendations}
          onSearch={this.handleRecSearch}
        />
      );
    } else {
      body = (
        <ListPage
          apps={apps}
          appDigestProps={APP_DIGEST_PROPS}
          fetchMore={({ startIndex }) => fetchMore({ startIndex, text })}
        />
      );
    }

    if (error) {
      body = <ErrorPage>{error.message}</ErrorPage>;
    } else if (loading || !userSetInput) {
      body = <LoadingPage headerHeight={56} />;
    }

    return (
      <Layout
        center={`Search for "${text}"`}
        name="Search"
        scrollKey="search"
        pageElement={body}
        navbarContent={this.renderSearchNavContent()}
      />
    );
  }

  /**
   * This is an ugly hack used to fix the automation tests for IOS
   * For some strange reason IOS does not see the virtualized lists to click on items or interact with
   * This fixes a very simple use case if there are less than 10 search results then it will use a simple list not a virtualized on
   * @returns {*}
   */
  getList() {
    const { isApp, apps } = this.props;

    if (apps.length <= 10) {
      return isApp ? AppListSimple : ClockListSimple;
    }

    return isApp ? AppList : ClockList;
  }
}

export default connectDeep(mapStateToProps, mapDispatchToProps)(Search);
